import { ensureRunning, getAudioContext } from './audioUnlock';

/**
 * One short tone, reachable only from `VisualOnly`.
 *
 * This is the **only** audible signal in the software that is not speech, and its single purpose
 * is that total speech failure is never silent: if every provider has failed, the person has
 * typed something and nothing has come out, and a partner in the room needs a reason to look at
 * the screen.
 *
 * It is explicitly **not** an alarm. FR7 was withdrawn — a call-bell is a life-safety function
 * and this stack is wrong for it: it needs the iPad awake, charged, in reach, unlocked,
 * foregrounded and audible. That job belongs to the physical wireless button in the delivered
 * kit. So there is no ALARM button, and nothing calls this except the fallback provider.
 */

const FREQUENCY_HZ = 880;
const DURATION_S = 0.28;
const PEAK_GAIN = 0.35;

export async function playAttentionTone(): Promise<boolean> {
  const running = await ensureRunning();
  const ctx = getAudioContext();
  if (!ctx || !running) return false;

  try {
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();

    oscillator.type = 'sine';
    oscillator.frequency.value = FREQUENCY_HZ;

    // Ramped rather than switched: an instant edge clicks, and a click sounds like a fault
    // rather than a signal.
    const start = ctx.currentTime;
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(PEAK_GAIN, start + 0.02);
    gain.gain.linearRampToValueAtTime(0, start + DURATION_S);

    oscillator.connect(gain).connect(ctx.destination);
    oscillator.start(start);
    oscillator.stop(start + DURATION_S);

    return true;
  } catch (err) {
    console.error('[audio] attention tone failed', err);
    return false;
  }
}
